import React from "react";
import { IQuantityControlProps } from "@model/UIComponents.model";
import styles from "./UiLibraryComponent.module.css";
import { useQuantityControl } from "../../../Logic/Functions/Hooks/useQuantityControl";
import PlusIcon from "Utils/Assets/SVGs/PlusIcon";
import MinusIcon from "Utils/Assets/SVGs/MinusIcon";

const QuantityControl: React.FC<IQuantityControlProps> = ({
  value = 1,
  onChange,
  onPreviewChange,
  step = 1,
  min = 1,
  max,
  customContainerClass = "",
  inputClass = "",
  incrementButtonClass = "",
  decrementButtonClass = "",
  disabled = false,
  onError,
}) => {
  const {
    inputValue,
    error,
    handleInputChange,
    handleInputBlur,
    handleIncrease,
    handleDecrease,
    disableIncrement,
    disableDecrement,
  } = useQuantityControl({
    value,
    onChange,
    onPreviewChange,
    step,
    min,
    max,
    disabled,
    onError,
  });

  return (
    <div className={`${styles.inputContainer} ${customContainerClass}`} data-testid="quantity-control-container">
      <div className={styles.quantityWrapper}>
        <button
          type="button"
          onClick={handleDecrease}
          className={`${styles.quantityButton} ${decrementButtonClass} ${disableDecrement ? styles.disabledButton : ""}`}
          data-testid="quantity-decrement"
          disabled={disableDecrement}
          aria-label="Decrease quantity"
        >
          <MinusIcon width={14} height={14} />
        </button>

        <input
          type="number"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          disabled={disabled}
          min={min}
          max={max}
          className={`${styles.quantityInput} ${inputClass} ${error ? styles.inputWithError : ""} ${disabled ? styles.disabledInput : ""}`}
          data-testid="quantity-input"
          inputMode="numeric"
          pattern="[0-9]*"
          onKeyDown={(e) => {
            const disallowed = ["e", "E", "+", "-"];
            if ((step ?? 1) % 1 === 0) disallowed.push(".");
            if (disallowed.includes(e.key)) e.preventDefault();
          }}
          aria-invalid={!!error}
          aria-describedby={error ? "quantity-error" : undefined}
        />

        <button
          type="button"
          onClick={handleIncrease}
          className={`${styles.quantityButton} ${incrementButtonClass} ${disableIncrement ? styles.disabledButton : ""}`}
          data-testid="quantity-increment"
          disabled={disableIncrement}
          aria-label="Increase quantity"
        >
          <PlusIcon width={14} height={14} />
        </button>
      </div>

      {error && (
        <div
          id="quantity-error"
          className={styles.errorMessageWarning}
          data-testid="quantity-error"
          role="alert"
          aria-live="polite"
        >
          {error}
        </div>
      )}
    </div>
  );
};

export default QuantityControl;
